import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Award, Edit, Archive, Info } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchResourceLevels,
  createResourceLevel,
  updateResourceLevel,
  deleteResourceLevel,
  clearError,
} from '@/store/slices/resourceLevelsSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassCheckbox,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
} from '@/design-system';
import ResourceLevelForm from './components/ResourceLevelForm';
import type { ResourceLevel, Column } from '@/types';

const ResourceLevelsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { entities: resourceLevels, loading, error } = useSelector((state: RootState) => state.resourceLevels);
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [selectedResourceLevel, setSelectedResourceLevel] = useState<ResourceLevel | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const isAdmin = currentUser?.role === 'ADMIN';

  useEffect(() => {
    dispatch(fetchResourceLevels(showArchived));
  }, [dispatch, showArchived]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleCreateResourceLevel = async (data: Partial<ResourceLevel>) => {
    const result = await dispatch(createResourceLevel(data));
    if (createResourceLevel.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
      dispatch(fetchResourceLevels(showArchived));
    }
  };

  const handleUpdateResourceLevel = async (data: Partial<ResourceLevel>) => {
    if (!selectedResourceLevel) return;
    const result = await dispatch(updateResourceLevel({ id: selectedResourceLevel.id, data }));
    if (updateResourceLevel.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedResourceLevel(null);
      dispatch(fetchResourceLevels(showArchived));
    }
  };

  const handleArchiveResourceLevel = async () => {
    if (!selectedResourceLevel) return;
    const result = await dispatch(deleteResourceLevel(selectedResourceLevel.id));
    if (deleteResourceLevel.fulfilled.match(result)) {
      setIsArchiveModalOpen(false);
      setSelectedResourceLevel(null);
      dispatch(fetchResourceLevels(showArchived));
    }
  };

  const getCategoryBadgeVariant = (category: string): 'info' | 'success' | 'warning' | 'default' => {
    switch (category) {
      case 'TECHNICAL':
        return 'info';
      case 'MANAGEMENT':
        return 'success';
      case 'SPECIALIZED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: Column<ResourceLevel>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      render: (value) => (
        <span className="font-mono font-medium text-[var(--text)]">{value}</span>
      ),
    },
    {
      key: 'displayName',
      header: 'Display Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <span className="font-medium text-[var(--text)]">{value}</span>
          {row.description && (
            <p className="text-xs text-[var(--muted)] mt-0.5">{row.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (value) => (
        <GlassBadge variant={getCategoryBadgeVariant(value as string)} size="sm">
          {value}
        </GlassBadge>
      ),
    },
    {
      key: 'sortOrder',
      header: 'Sort Order',
      sortable: true,
      render: (value) => (
        <span className="text-center text-[var(--text)]">{value}</span>
      ),
    },
    {
      key: '_count',
      header: 'Rate Cards',
      render: (value) => (
        <span className="text-[var(--muted)]">{value?.rateCards || 0}</span>
      ),
    },
    {
      key: '_count',
      header: 'Resources',
      render: (value) => (
        <span className="text-[var(--muted)]">{value?.resources || 0}</span>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value) => (
        <GlassBadge variant={value ? 'success' : 'default'} size="sm">
          {value ? 'Active' : 'Archived'}
        </GlassBadge>
      ),
    },
  ];

  const tableActions = isAdmin ? [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (row: ResourceLevel) => {
        setSelectedResourceLevel(row);
        setIsEditModalOpen(true);
      },
    },
    {
      label: 'Archive',
      icon: Archive,
      variant: 'danger' as const,
      onClick: (row: ResourceLevel) => {
        if (row.isActive) {
          setSelectedResourceLevel(row);
          setIsArchiveModalOpen(true);
        }
      },
      hidden: (row: ResourceLevel) => !row.isActive,
    },
  ] : [];

  if (loading && resourceLevels.length === 0) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading resource levels..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Resource Levels"
          subtitle="Manage resource level definitions and metadata"
          icon={<Award className="w-6 h-6" />}
          iconGradient="cyan"
          className="mb-6"
          actions={
            isAdmin && (
              <GlassButton
                variant="primary"
                size="sm"
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add Resource Level
              </GlassButton>
            )
          }
        />
      </SectionReveal>

      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      <SectionReveal delay={0.1}>
        <GlassCard padding="none">
          <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)]">
                Resource Levels ({resourceLevels.length})
              </h3>
              <p className="text-sm text-[var(--muted)]">
                Skill and experience tier definitions
              </p>
            </div>
            <GlassCheckbox
              checked={showArchived}
              onChange={(checked) => setShowArchived(checked)}
              label="Show Archived"
            />
          </div>
          <div className="max-h-[calc(100vh-450px)] overflow-auto">
            <DataTable
              data={resourceLevels}
              columns={columns}
              actions={tableActions}
              sortable
              pagination
              pageSize={15}
              emptyMessage="No resource levels found"
            />
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Info Card */}
      <SectionReveal delay={0.2}>
        <GlassCard className="mt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-accent-blue/10">
              <Info className="w-5 h-5 text-accent-blue" />
            </div>
            <div>
              <h4 className="font-semibold text-[var(--text)] mb-2">Resource Level Management</h4>
              <ul className="text-sm text-[var(--muted)] space-y-1">
                <li>• Resource levels define the skill/experience tiers for team members</li>
                <li>• Each level has a unique code (e.g., T1, M2) and display name</li>
                <li>• Levels are categorized as Technical, Management, or Specialized</li>
                <li>• Sort order controls display sequence in dropdowns throughout the app</li>
                <li>• Archiving a level requires it to have no associated rate cards or resources</li>
              </ul>
            </div>
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Floating Add button */}
      {isAdmin && (
        <button
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
          onClick={() => setIsCreateModalOpen(true)}
          aria-label="Add Resource Level"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add Resource Level"
        subtitle="Define a new skill/experience tier"
        icon={<Award className="w-5 h-5" />}
        iconGradient="cyan"
        size="md"
      >
        <ResourceLevelForm
          onSubmit={handleCreateResourceLevel}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={loading}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedResourceLevel(null);
        }}
        title="Edit Resource Level"
        subtitle={selectedResourceLevel?.displayName}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="md"
      >
        <ResourceLevelForm
          initialData={selectedResourceLevel}
          onSubmit={handleUpdateResourceLevel}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedResourceLevel(null);
          }}
          isLoading={loading}
        />
      </GlassModal>

      {/* Archive Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isArchiveModalOpen}
        onClose={() => {
          setIsArchiveModalOpen(false);
          setSelectedResourceLevel(null);
        }}
        onConfirm={handleArchiveResourceLevel}
        title="Archive Resource Level"
        message={
          selectedResourceLevel
            ? `Are you sure you want to archive ${selectedResourceLevel.displayName}? This level will no longer appear in dropdowns.`
            : ''
        }
        confirmLabel="Archive"
        variant="warning"
        isLoading={loading}
      />
    </div>
  );
};

export default ResourceLevelsPage;
